import { Dao } from '../dao';
import RootGizmo from '../gizmos/root-gizmo';
import { rootGizmoConfigV2ToV3, rootGizmoDataV2ToV3 } from '../gizmos/utils';
import {
  IGizmo,
  IRootGizmoCallbacks,
  IRootGizmoData,
  TSubmitRootGizmoData,
  TOnSubmit,
  IPlatformCallbacks,
  IPlatformInterface,
  IRealmFormGizmo,
  IGizmoEvent,
  TSendGlobalEventCB,
  IRealmFilledForm,
} from '../types';
import { submitRootGizmoDataCB } from './foundation-functions';
import FlowyGlobal from './flowy-global';

interface IFlowyFoundationConstructor {
  /**
   * Instance of the Global variable, it exists only one of this in the whole application
   */
  flowyGlobal?: FlowyGlobal;
  /**
   * Parent RootGizmo's Flowy id (when it's an annex form)
   */
  parentRootGizmoFid?: string;
  /**
   * Configuration of the root gizmo
   */
  config?: IGizmo;
  /**
   * This will happen when we have 'annex_forms'
   */
  configs?: IGizmo[];
  data?: IRootGizmoData;
  /**
   * If provided this function is called when the user has clicked on save the form.
   */
  onSubmit?: TOnSubmit;
  /**
   * Object containing the functions used by the core in order to interact with the
   * platform.
   */
  platformCallbacks?: IPlatformCallbacks;
  /**
   * Class instance provided by the Platform in order to interact with the platform
   */
  platformInterface?: IPlatformInterface;
  /**
   * Flowy API url
   */
  apiUrl?: string;
  /**
   * Params comming from the platform layer
   */
  params: any;
  // onSubmit?: (props: { rootGizmoData: IRootGizmoData }) => Promise<void>;
  //! project
  // rootGizmoFid
  // parentRootGizmoFid
  // onSubmit?: () => Promise<void>;
}

/**
 * It's job is to be the base for the RootGizmo and to remove the
 */
class FlowyFoundation {
  private _config?: IGizmo;
  private _rootGizmo?: RootGizmo;
  private _parentRootGizmoFid?: string;
  private _data?: IRootGizmoData;
  private _onSubmit?: TOnSubmit;
  private _platformCallbacks?: IPlatformCallbacks;
  private _platformInterface?: IPlatformInterface;
  private _apiUrl?: string;
  private _dao?: Dao;
  /**
   * Reference to the global FlowyGlobal instance (pun intended)
   */
  private _flowyGlobal?: FlowyGlobal;
  /**
   * Parametes arriving from the platform layer
   */
  private _params: any;

  constructor({
    flowyGlobal,
    config,
    parentRootGizmoFid,
    data,
    onSubmit,
    platformCallbacks,
    platformInterface,
    apiUrl,
    params,
  }: IFlowyFoundationConstructor) {
    this._flowyGlobal = flowyGlobal;
    this._config = config;
    this._parentRootGizmoFid = parentRootGizmoFid;
    this._data = data;
    this._onSubmit = onSubmit;
    this._platformCallbacks = platformCallbacks;
    this._apiUrl = apiUrl;
    this._platformInterface = platformInterface;
    this._params = params;
  }

  public init = (): Promise<FlowyFoundation> => {
    return new Promise<FlowyFoundation>(async resolve => {
      let config: IGizmo | undefined = this._config;
      const user = await this._platformInterface?.getUser();
      const database = await this._flowyGlobal?.getDatabase();

      // Subscribing to the FlowyGlobal events
      this._flowyGlobal?.globalEvents.subscribe(this._handleGlobalEvents);

      if (this._apiUrl) {
        this._dao = new Dao({
          apiUrl: this._apiUrl,
          user,
          database,
          foundation: this,
        });
        await this._dao.init();
      }

      if (!config && this._dao && this._params && this._params['rgfid']) {
        if (!this._parentRootGizmoFid) {
          const configs = await this._dao.getRootGizmoConfigs({
            fid: this._params['rgfid'],
            publicRG: this._params['public'],
          });

          // try {
          //   const database = this._dao.getDatabase();

          //   if (database) {
          //     const savedFilledForms = await database.search({
          //       type: 'filledForms',
          //       params: {},
          //     });
          //     console.log('sssssssssssssavedFilledForms', savedFilledForms);

          //     if (savedFilledForms && savedFilledForms.length > 0) {
          //       for (const savedFilledForm of savedFilledForms) {
          //         await database.delete({
          //           type: 'filledForms',
          //           fid: savedFilledForm.fid,
          //         });
          //       }
          //     }
          //   }
          // } catch (e) {}

          if (configs.length > 0) {
            config = configs[0];
          }
        } else if (database) {
          // Since we have a parentRootGizmoFid it means we have the configurations saved in the
          // database.
          const savedConfig: IRealmFormGizmo = await database.get({
            type: 'forms',
            fid: this._params['rgfid'],
          });
          if (this._params['data-fid']) {
            try {
              const savedData: IRealmFilledForm = await database.get({
                type: 'filledForms',
                fid: this._params['data-fid'],
              });

              if (savedData?.gizmoData) {
                this._data = rootGizmoDataV2ToV3({ data: savedData.gizmoData });
              }
            } catch (e) {}
          }

          if (savedConfig?.formGizmo) {
            config = rootGizmoConfigV2ToV3({
              config: savedConfig?.formGizmo,
            });
          }
        }
      } else if (
        config?.ops?.form?.auth?.rootGizmoFid &&
        config.ops.form.auth.endpoint === 'signup' &&
        this._dao
      ) {
        const rootGizmoFid = config.ops.form.auth.rootGizmoFid;

        // This means we have a form configured to get the signup form from the server
        // and assign it to the config.
        const configs = await this._dao.getFormSignup({
          fid: rootGizmoFid,
        });

        if (configs.length > 0) {
          config = configs[0];
          this._config = config;
        }
      }

      if (
        !this._data &&
        this._dao &&
        this._params &&
        this._params['rgfid'] &&
        this._params['fid']
      ) {
        const data = await this._dao.getRootGizmoData({
          rgfid: this._params['rgfid'],
          fid: this._params['fid'],
        });

        if (data.length > 0) {
          this._data = data[0];
        }
      }

      if (config) {
        const callbacks: IRootGizmoCallbacks = {
          submitRootGizmoDataCB: this._submitRootGizmoDataCB,
          sendGlobalEventCB: this._sendGlobalEventsCB,
        };

        const rootGizmo: RootGizmo = new RootGizmo({
          config,
          parentRootGizmoFid: this._parentRootGizmoFid,
          data: this._data,
          callbacks,
          platformCallbacks: this._platformCallbacks,
          dao: this._dao,
          params: this._params,
          foundation: this,
        });
        await rootGizmo.init();
        this._rootGizmo = rootGizmo;

        const rootGizmoDataFid = rootGizmo.getInfoCB({
          type: 'rootGizmoDataFid',
        });

        this._flowyGlobal?.registerFoundation({
          fid: rootGizmoDataFid,
          foundation: this,
        });
      }

      resolve(this);
    });
  };

  public end = async () => {
    if (this._rootGizmo) {
      const rootGizmoDataFid = this._rootGizmo.getInfoCB({
        type: 'rootGizmoDataFid',
      });

      this._flowyGlobal?.unregisterFoundation({
        fid: rootGizmoDataFid,
      });
    }
  };

  /**
   * Get the FlowyGlobal instance
   * @returns
   */
  public getGlobal = (): FlowyGlobal | undefined => {
    return this._flowyGlobal;
  };

  /**
   * Get the RootGizmo
   * @returns {RootGizmo} root gizmo
   */
  public getRootGizmo = (): RootGizmo | undefined => {
    return this._rootGizmo;
  };

  /**
   * Get the PlatformInterface
   * @returns {IPlatformInterface} platform interface
   */
  public getPlatformInterface = (): IPlatformInterface | undefined => {
    return this._platformInterface;
  };

  /**
   * Get the PlatformCallbacks
   * @returns {IPlatformCallbacks} platform callbacks
   */
  public getPlatformCallbacks = (): IPlatformCallbacks | undefined => {
    return this._platformCallbacks;
  };

  private _submitRootGizmoDataCB: TSubmitRootGizmoData = ({
    rootGizmoData,
    onSubmitFinish,
  }): Promise<void> => {
    return new Promise<void>(async resolve => {
      await submitRootGizmoDataCB({
        rootGizmoData,
        onSubmitFinish,
        context: {
          dao: this._dao,
          flowyGlobal: this._flowyGlobal,
          onSubmit: this._onSubmit,
          params: this._params,
          parentRootGizmoFid: this._parentRootGizmoFid,
          platformCallbacks: this._platformCallbacks,
        },
      });

      resolve();
    });
  };

  /**
   * Used by the RootGizmo to send events to the global event bus
   *
   * @param {IGizmoEvent} event event being sent
   * @returns
   */
  private _sendGlobalEventsCB: TSendGlobalEventCB = ({
    event,
  }): Promise<void> => {
    return new Promise<void>(async resolve => {
      await this._flowyGlobal?.sendEvent({ event });
      resolve();
    });
  };

  /**
   * Callback tohandle the events sent by the FlowyGlobal layer
   *
   * @param {IGizmoEvent} event event being received
   * @returns
   */
  private _handleGlobalEvents = (event: IGizmoEvent): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (
        this._rootGizmo &&
        event.dest?.rootGizmoFid === this._rootGizmo?.getConfig().fid
      ) {
        this._rootGizmo.sendEvent(event);
      }
      resolve();
    });
  };
}

export default FlowyFoundation;
